// @ts-strict-ignore
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { TabsetComponent } from 'ngx-bootstrap/tabs';
import { ArticleAccessoryEntryVO } from '@article/model/vo/article-accessory-info-vo';
import { AdminToolService } from '../../../services/admintool.service';
import { MenuItem } from '@project/service/menu/menu.interfaces';
import { AlertHelper } from '@shared/alert/alert.helper';
import { ArticleVO } from '@article/model/vo/article.vo';
import { ArticleDataService } from '@article/service/article-data.service';
import { ArticleComponentsPresenter } from '../../presenter/article-components.presenter';
import { SharedComponentsPresenter } from '@shared/shared-components.presenter';

@Component({
  standalone: false,
  templateUrl: './manage-accessories.component.html',
  providers: [TabsetComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManageAccessoriesComponent implements OnInit {

  public update = new EventEmitter<void>();

  public accessoriesList: ArticleAccessoryEntryVO[];

  @Input() articleId: number;

  constructor(
    private bsModalRef: BsModalRef,
    private cdRef: ChangeDetectorRef,
    private alertHelper: AlertHelper,
    private adminToolService: AdminToolService,
    private articleDataService: ArticleDataService,
    private articleComponentsPresenter: ArticleComponentsPresenter,
    private sharedComponentsPresenter: SharedComponentsPresenter,
  ) {}

  public ngOnInit(): void {
    this.adminToolService.getAccessoryEntries(this.articleId).subscribe((accessories: ArticleAccessoryEntryVO[]) => {
      this.accessoriesList = accessories;
      this.cdRef.markForCheck();
    });
  }

  public getHeaderMenu(): MenuItem[] {
    const items: MenuItem[] = [];
    items.push({
      label: 'global.label.add',
      action: () => this.openAddAccessoryDialog(),
      icon: 'plus',
      enabled: true,
      visible: true,
    });
    items.push({
      label: 'global.label.delete_all',
      action: () => this.deleteAll(),
      icon: 'delete',
      enabled: true,
      visible: true,
    });
    items.push({
      label: 'manageAccessories.header.copyAccessoriesFrom',
      action: () => this.openCopyAccessoryFromDialog(),
      icon: 'copy',
      enabled: true,
      visible: true,
    });

    return items;
  }

  public getAccessoryMenu(accessory: ArticleAccessoryEntryVO): MenuItem[] {
    const items: MenuItem[] = [];
    items.push({
      label: 'global.label.edit',
      action: () => this.openEditAccessoryDialog(accessory),
      icon: 'edit-pen',
      enabled: true,
      visible: true,
    });
    items.push({
      label: 'global.label.delete',
      action: () => this.deleteAccessory(accessory),
      icon: 'delete',
      enabled: true,
      visible: true,
    });

    return items;
  }

  public close(): void {
    this.bsModalRef.hide();
  }

  public save(): void {
    this.adminToolService.saveAccessoryEntries(this.articleId, this.accessoriesList).subscribe(() => this.close());
  }

  private openAddAccessoryDialog(): void {
    this.articleComponentsPresenter.openAddAccessoryPopup().then((articleAccessory: ArticleAccessoryEntryVO) => {
      const isNewAccessory: boolean = !this.accessoriesList.some((accessory: ArticleAccessoryEntryVO) => {
        return accessory.accessoryArticleId === articleAccessory.accessoryArticleId;
      });
      if (isNewAccessory) {
        this.accessoriesList.push(articleAccessory);
        this.sortAccessoryList();
        this.cdRef.markForCheck();
      } else {
        this.alertHelper.showError(
          'manageAccessories.error.title.article_accessory_exists',
          'manageAccessories.error.message.article_accessory_exists',
          {
            translationParams: [articleAccessory.accessoryArticleId.toString()],
          }
        );
      }
    });
  }

  private deleteAll(): void {
    this.accessoriesList = [];
  }

  private openCopyAccessoryFromDialog(): void {
    this.sharedComponentsPresenter.openCopyFromPopup('accessory').then((accessoryArticleSource: string) => {
      this.articleDataService.loadArticleByIdOrArticleNumberAndVersion(accessoryArticleSource).subscribe((article: ArticleVO) => {
        if (article) {
          this.adminToolService.getAccessoryEntries(article.identity).subscribe((accessories: ArticleAccessoryEntryVO[]) => {
            if (accessories) {
              accessories.map((accessoryEntry: ArticleAccessoryEntryVO) => {
                const commonAccessory: ArticleAccessoryEntryVO = this.accessoriesList.find(
                  (item: ArticleAccessoryEntryVO) => item.accessoryArticleId === accessoryEntry.accessoryArticleId
                );
                if (commonAccessory) {
                  const index: number = this.accessoriesList.indexOf(commonAccessory);
                  this.accessoriesList.splice(index, 1, accessoryEntry);
                } else {
                  this.accessoriesList.push(accessoryEntry);
                }
              });
              this.sortAccessoryList();
              this.cdRef.markForCheck();
            } else {
              this.alertHelper.showError(
                'manageAccessories.error.title.noArticleAccessoryList',
                'manageAccessories.error.message.noArticleAccessoryList'
              );
            }
          });
        } else {
          this.alertHelper.showError('searchArticleView.title.no_articles_found', 'searchArticleView.message.no_articles_found', {
            translationParams: [accessoryArticleSource],
          });
        }
      });
    });
  }

  private sortAccessoryList(): void {
    this.accessoriesList.sort((a: ArticleAccessoryEntryVO, b: ArticleAccessoryEntryVO) => {
      return a.accessoryArticleId - b.accessoryArticleId;
    });
  }

  private openEditAccessoryDialog(accessory: ArticleAccessoryEntryVO): void {
    this.articleComponentsPresenter.openEditAccessoryPopup(accessory).then((rotationDelta: number) => {
      const current = this.accessoriesList.find((element: ArticleAccessoryEntryVO) => {
        return element.accessoryArticleId === accessory.accessoryArticleId;
      });
      current.rotationDelta = rotationDelta;
      this.cdRef.markForCheck();
    });
  }

  private deleteAccessory(accessory: ArticleAccessoryEntryVO): void {
    const index: number = this.accessoriesList.indexOf(accessory);
    this.accessoriesList.splice(index, 1);
  }
}
